import { Component, EventEmitter, Input, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { AlertInfo } from '../../../../models/alert-info';
import { SavedDataInfo } from '../../../../models/form/saved-data-info';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { FormService } from '../../../../services/form.service';
import { RequestsService } from '../../../../services/api/requests.service';
import { ValidatorService } from '../../../../services/validator.service';

import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { InputFieldComponent } from '../../../../components/form/input-field/input-field.component';
import { CustomerService } from 'src/app/services/api/customer.service';
import { AutocompleteFieldComponent } from 'src/app/components/form/autocomplete-field/autocomplete-field.component';

@Component({
  selector: 'requests-form',
  imports: [NgbAlert, InputFieldComponent, ReactiveFormsModule, AutocompleteFieldComponent],
  templateUrl: './requests-form.component.html',
  styleUrl: './requests-form.component.scss'
})
export class RequestsFormComponent {

  @Input() request?: any;
  @Input() readonlyRequestForm: boolean = false;
  @Input() requestId: number | null = null;
  @Input() isEditPage: boolean = false;
  @Output() onSavedData = new EventEmitter<SavedDataInfo>();

  form!: FormGroup;

  alertInfo: AlertInfo = { show: false, type: '', message: '' };
  
  @ViewChildren(AutocompleteFieldComponent) autocompleteFields!: QueryList<AutocompleteFieldComponent>;

  constructor(
    private validatorService: ValidatorService,
    private formService: FormService,
    private alertNotificationService: AlertNotificationService,
    private requestsService: RequestsService,
    protected customerService: CustomerService
  ) {}

  ngOnInit(): void {
    const isCompanyDisabled = this.isEditPage 
      ? false 
      : true;
    
    this.form = new FormGroup({
      company: new FormControl({ value: '', disabled: isCompanyDisabled }, Validators.required),
      clientType: new FormControl({ value: '', disabled: true }, Validators.required),
      firstName: new FormControl({ value: '', disabled: true }, Validators.required),
      lastName: new FormControl({ value: '', disabled: true }, Validators.required),
      mobilePhone: new FormControl({ value: '', disabled: true }, Validators.required),
      email: new FormControl({ value: '', disabled: true }, Validators.required),
      customerId: new FormControl(null)
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['request'] && changes['request'].currentValue) {
      this.disableAutocompleteDropdowns();
      
      this.form.patchValue({
        company: this.request.customer.companyName,
        clientType: this.request.customerType.name,
        firstName: this.request.customerFirstName,
        lastName: this.request.customerLastName,
        mobilePhone: this.request.customerPhone,
        email: this.request.customerEmail,
        customerId: this.request.customer.customerId
      });
      

      const companyControl = this.form.get('company');
      if (companyControl) {
        const isCompanyDisabled = this.isEditPage
          ? (this.request?.status !== 'OPEN')
          : true;
          
        if (isCompanyDisabled) {
          companyControl.disable();
        } else {
          companyControl.enable();
        }
      }
      
      setTimeout(() => {
        this.enableAutocompleteDropdowns(300);
      }, 100);
    }
  }

  fetchClientPersons(customerId: number): void {
    if (!customerId) return;
    this.customerService.listClientPersons(customerId).subscribe({
      next: (response: any) => {
        if (response && response.data && response.data.customerPersons) {
          const customerPersons = response.data.customerPersons;
          const decisionMaker = customerPersons.find((person: any) => person.isDecisionMaker === true);

          if (decisionMaker) {
            this.form.patchValue({
              firstName: decisionMaker.firstName || '',
              lastName: decisionMaker.lastName || '',
              mobilePhone: decisionMaker.phone || '',
              email: decisionMaker.email || ''
            });
          } else {
            this.alertNotificationService.error('Error retrieving customer contacts');
          }
        }
      },
      error: (error: any) => {
        console.error('Error fetching client persons:', error);
        this.alertNotificationService.error('Error retrieving customer contacts');
      }
    });
  }

  /**
   * *******************
   * Submit form methods
   * *******************
   */
  showAlertInfo(type: string, message: string) {
    this.alertInfo = {
      show: true,
      type,
      message
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }
  updateValidationErrors(validationErrors: any) {
    this.formService.resetErrors(this.form);
    this.formService.setValidationErrors(this.form, validationErrors);
  }
  submitFormSuccess(response: any) {
    this.alertNotificationService.success('Operation completed successfully')
    this.showAlertInfo('success', 'Operation completed successfully');

    setTimeout(() => {
      this.enableAutocompleteDropdowns(1000);
    }, 500);

    this.onSavedData.emit({
      success: true,
      response
    });
  }
  submitFormError(error: any) {
    setTimeout(() => {
      this.enableAutocompleteDropdowns(1000);
    }, 500);
    
    if (error.error.error.description == 'ARGUMENT_VALIDATION_ERROR') {
      this.updateValidationErrors(error.error.error.validationErrors);
    }
    else if (error.error.error.description == 'USER_EMAIL_ALREADY_EXISTS') {
      this.updateValidationErrors([{ field: 'email', defaultMessage: error.error.error.message }]);
      this.showAlertInfo('danger', `Please fix the error: ${error.error.error.message}`);
    }
    else {
      this.showAlertInfo('danger', error.error.error.message);
    }

    this.onSavedData.emit({
      success: false,
      error
    });

  }
  submitForm(): void {
    this.disableAutocompleteDropdowns();
    const formValues = this.getFormValuesIncludingDisabled();

    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control?.markAsTouched();
      control?.updateValueAndValidity();
    });

    if (this.form.valid) {
      const values = {
        ...formValues,
        tenantId: 1
      };

      this.alertInfo.show = false;
      this.formService.resetErrors(this.form);

      const serviceMethod = this.request ? this.requestsService.update(this.request.requestId, values) : this.requestsService.create(values);
      serviceMethod.subscribe({
        next: (response: any) => {
          this.submitFormSuccess(response);
        },
        error: (error: any) => {
          this.submitFormError(error);
        }
      });
    } else {
      this.showAlertInfo('danger', 'Please fill in all required fields');
    }
  }

  getFormValuesIncludingDisabled(): any {
    const formValues: any = {};

    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      formValues[key] = control?.value;
    });

    return {
      company: formValues.company,
      clientType: formValues.clientType,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      mobilePhone: formValues.mobilePhone,
      email: formValues.email,
      customerId: formValues.customerId
    };
  }

  getFormErrors(): any {
    const errors: any = {};
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      if (control && control.errors) {
        errors[key] = control.errors;
      }
    });
    return errors;
  }


  private disableAutocompleteDropdowns(): void {
    if (this.autocompleteFields) {
      this.autocompleteFields.forEach(field => {
        field.setPreventAutoOpen(true);
        field.isDropdownOpen = false;
      });
    }
  }

  private enableAutocompleteDropdowns(delay: number = 2000): void {
    setTimeout(() => {
      if (this.autocompleteFields) {
        this.autocompleteFields.forEach(field => {
          field.setPreventAutoOpen(false);
        });
      }
    }, delay);
  }

  /**
   * *******************
   * Submit form methods
   * *******************
   */

  getRequestCompanyAutocomplete(): FormControl { return this.getFormControl('company'); }
  getRequestClientTypeInput(): FormControl { return this.getFormControl('clientType'); }
  getRequestNameInput(): FormControl { return this.getFormControl('firstName'); }
  getRequestSurnameInput(): FormControl { return this.getFormControl('lastName'); }
  getRequestTelInput(): FormControl { return this.getFormControl('mobilePhone'); }
  getRequestEmailInput(): FormControl { return this.getFormControl('email'); }

  getFormControl(name: string): FormControl { return this.form.get(name) as FormControl; }

  onCompanySelected(customerId: any): void {
    this.disableAutocompleteDropdowns();

    if (!customerId) {
      this.form.patchValue({
        customerId: null,
        clientType: '',
        firstName: '',
        lastName: '',
        mobilePhone: '',
        email: ''
      });
      setTimeout(() => this.enableAutocompleteDropdowns(500), 100);
      return;
    }

    const id = Number(customerId);
    this.form.patchValue({ customerId: id });

    this.customerService.detail(id).subscribe({
      next: (response: any) => {
        const customer = response?.data?.customer;
        if (customer) {
          this.form.patchValue({
            clientType: customer.customerType?.name || ''
          });
          this.fetchClientPersons(id);
        }
        setTimeout(() => this.enableAutocompleteDropdowns(500), 100);
      },
      error: () => {
        setTimeout(() => this.enableAutocompleteDropdowns(500), 100);
      }
    });
  }

  onSearchValueChange(value: any): void {
    this.disableAutocompleteDropdowns();
    setTimeout(() => {
      this.enableAutocompleteDropdowns(500);
    }, 100);
  } 
}